// src/components/TutorialSettings.jsx

import React, { useState } from 'react';
import { X, User, BarChart2, Mail, Settings, CheckCircle, ArrowRight } from 'lucide-react';

const TutorialSettings = ({ isVisible, onClose }) => {
  const [step, setStep] = useState(0);

  if (!isVisible) return null;

  // Define the tutorial steps based on the Settings Page features
  const steps = [
    {
      title: "Settings Overview",
      icon: <Settings size={24} />,
      color: "text-gray-500 dark:text-gray-200", // Adjusted for visibility
      bg: "bg-gray-100 dark:bg-gray-700/20",
      description: "Welcome to the Settings Hub. Here you can manage your account details, notifications, and app preferences."
    },
    {
      title: "Profile",
      icon: <User size={24} />,
      color: "text-blue-500 dark:text-blue-400",
      bg: "bg-blue-100 dark:bg-blue-500/20",
      description: "Manage your personal identity. Update your name, email address, and other account details to keep your profile up to date."
    },
    {
      title: "Performance Metrics",
      icon: <BarChart2 size={24} />,
      color: "text-emerald-500 dark:text-emerald-400",
      bg: "bg-emerald-100 dark:bg-emerald-500/20",
      description: "Generate a detailed trading matrix summary. This is perfect for sharing your stats with prop firms or mentors."
    },
    {
      title: "Support",
      icon: <Mail size={24} />,
      color: "text-teal-500 dark:text-teal-400",
      bg: "bg-teal-100 dark:bg-teal-500/20",
      description: "Need help? Click here to submit a support ticket. This will open your email client so you can directly contact our team."
    }
  ];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const currentStep = steps[step];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 dark:bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col h-[500px]">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-100 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-6 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Settings Tour</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 mt-4 rounded-full overflow-hidden">
            <div 
              className="bg-blue-500 h-full transition-all duration-300 ease-out"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 flex-grow flex flex-col items-center justify-center text-center bg-slate-50 dark:bg-gray-900">
          <div className={`p-4 rounded-2xl ${currentStep.bg} ${currentStep.color} mb-6 shadow-sm`}>
            {currentStep.icon}
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{currentStep.title}</h3>
          
          <p className="text-gray-500 dark:text-gray-400 text-base leading-relaxed">
            {currentStep.description}
          </p>

          {/* Step Indicators (Dots) */}
          <div className="flex gap-2 mt-8">
            {steps.map((_, idx) => (
              <div 
                key={idx} 
                className={`h-2 rounded-full transition-all duration-300 ${idx === step ? 'w-6 bg-blue-500' : 'w-2 bg-gray-300 dark:bg-gray-700'}`}
              />
            ))}
          </div>
        </div>

        {/* Footer / Navigation */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 flex-shrink-0">
          <div className="flex gap-3">
            {step > 0 ? (
              <button
                onClick={handleBack}
                className="px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium w-1/3"
              >
                Back
              </button>
            ) : (
              <div className="w-1/3"></div> // Spacer
            )}
            
            <button
              onClick={handleNext}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 group shadow-sm"
            >
              {step === steps.length - 1 ? 'Finish' : 'Next'}
              {step !== steps.length - 1 && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
              {step === steps.length - 1 && <CheckCircle size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialSettings;